/* site/SiteChrome.jsx — the shared site chrome, behaviour-identical to the prototype's
 * inline script: utility top bar (search + language), header (transparent over the home
 * film → solid on scroll → black glass while the mega menu is open), Products mega menu
 * (hover / toggle / Escape / outside click), mobile nav (Menu ⇄ Close, scroll lock,
 * Products accordion, utility block), search overlay, footer.
 *
 * Every prototype page ships this same chrome. It differs on exactly two points, both
 * driven by the `home` flag:
 *   · the header carries `solid-default` on every page EXCEPT the home film page
 *   · the footer opens with the statement row on every page EXCEPT home
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SiteLink, splitSubs, subHref, worldHref, toRoute } from "./siteLinks";
import { useSiteContent } from "./content/SiteContent";
import { WORLDS, SETTINGS } from "./content/shared";
import LANDING from "./content/landing";
import { loadIndex, search, groupResults } from "./siteSearch";
import { useSiteLang } from "./siteLangContext";
import { useCmsSettings } from "./cms/useCmsSettings";

/* The prototype's top bar carries a language switch. It selects the language the SITE is read
   in; the site's copy is CMS-authored English until a translated version exists, so this is
   presentation state only — it deliberately does not touch the platform locale (i18n/), which
   belongs to the signed-in apps. */
const LANGS = ["EN", "IT", "DE", "FR"];

export function useShared() {
  const worlds = useSiteContent("worlds", WORLDS);
  const settings = useSiteContent("settings", SETTINGS);
  /* the website builder's published settings (nav / logo / footer) — null until something is
     published, and then the chrome renders them instead of the built-in prototype defaults */
  const cms = useCmsSettings();
  return { worlds, settings, cms };
}

/** the builder's nav items when it has published an enabled navigation, else null */
const cmsNavItems = (cms) => (cms && cms.nav && cms.nav.enabled && Array.isArray(cms.nav.items) && cms.nav.items.length ? cms.nav.items : null);
const cmsChildren = (it) => (Array.isArray(it.children) ? it.children.filter((c) => c && c.label) : []);

/* ───────────────────────────── header ───────────────────────────── */
export function SiteHeader({ solid = false, home = false, onSearch }) {
  const { worlds, settings, cms } = useShared();
  const landing = useSiteContent("landing", LANDING);
  const { lang, setLang } = useSiteLang();      // shared with SiteLayout, which applies it
  const [scrolled, setScrolled] = useState(false);
  const [mega, setMega] = useState(false);
  const [mobile, setMobile] = useState(false);
  const itemRef = useRef(null);
  const closeTimer = useRef(null);
  /* The mega panel is a CHILD of the Products <li>, and the prototype keeps it open on :hover.
     In the prototype every click was a full page load, so it vanished with the page; in this SPA the
     route changes under a cursor that is still inside the panel, and :hover then holds the menu open
     over the new page. This locks the hover out until the pointer actually leaves the item. */
  const [hoverLocked, setHoverLocked] = useState(false);
  const location = useLocation();
  const path = location.pathname;
  /* the prototype marks the current page's nav item (li.is-current); Products covers every
     product-world route */
  const isCurrent = (it) => {
    if (it.mega) return /^\/(kitchens|systems|indoor|outdoor|products|collections)(\/|$)/.test(path);
    const r = toRoute(it.href).split("#")[0];
    return r !== "/" && (path === r || path.startsWith(r + "/"));
  };

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  useEffect(() => {
    setMega(false);
    setMobile(false);
    setHoverLocked(true);          // released by the mouseleave below
  }, [location.pathname, location.hash]);
  /* HOVER INTENT (the prototype's megaCloseTimer): closing on the bare mouseleave made the menu
     flicker as the cursor crossed the gap from the nav item into the panel. Open immediately,
     close 180 ms later, and cancel that close if the pointer comes back. */
  const openMega = useCallback(() => { clearTimeout(closeTimer.current); setMega(true); }, []);
  const closeMegaSoon = useCallback(() => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setMega(false), 180);
  }, []);
  useEffect(() => () => clearTimeout(closeTimer.current), []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setMega(false); };
    const onClick = (e) => { if (itemRef.current && !itemRef.current.contains(e.target)) setMega(false); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("click", onClick);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("click", onClick); };
  }, []);
  /* mobile menu open → scroll lock via an attribute (site.css), never by touching
     body.style.overflow, which would wipe the document-flow override SiteLayout sets */
  useEffect(() => {
    if (mobile) document.body.setAttribute("data-nav-open", "1"); else document.body.removeAttribute("data-nav-open");
    return () => document.body.removeAttribute("data-nav-open");
  }, [mobile]);

  /* scroll position ALONE drives the compact header, exactly as the prototype's updateHeaderState()
     does. Letting the open mega force it collapsed the 30px top bar and shrank the header mid-hover,
     which dropped the pointer out of the nav item and made the dropdown flicker open/closed. */
  const isScrolled = scrolled || solid;
  const nav = settings.nav;
  const cls = "site-header" + (home ? "" : " solid-default") + (isScrolled ? " is-scrolled" : "") + (mega ? " menu-open" : "");

  /* the Products mega menu item — ONE markup, rendered for the built-in nav's `mega` item and
     for a builder nav item flagged mega:true alike */
  const megaItem = (it) => (
        <li key={it.label} className={"has-dropdown" + (mega ? " is-open" : "") + (hoverLocked && !mega ? " is-hover-locked" : "") + (isCurrent(it) ? " is-current" : "")} id="productsNavItem" ref={itemRef}
          onMouseEnter={openMega} onMouseLeave={() => { setHoverLocked(false); closeMegaSoon(); }}>
          <SiteLink href={it.href}>{it.label}</SiteLink>
          <button type="button" className="dropdown-toggle" id="productsToggle" aria-expanded={mega} aria-controls="productsMenu" aria-label="Show product categories"
            onClick={(e) => { e.preventDefault(); setHoverLocked(false); setMega((v) => !v); }}><span className="caret" /></button>
          {/* clicking a link in the panel closes it even when it leads to the page you are already
              on — that navigation fires no route change, so the effect above would never run */}
          <div className="mega-menu" id="productsMenu"
            onClick={(e) => { if (e.target.closest && e.target.closest("a")) { setHoverLocked(true); setMega(false); } }}>
            <div className="mega-menu-inner">
              {worlds.map((w) => (
                <div className="mega-col" key={w.id}>
                  <SiteLink href={worldHref(w.id)} className="mega-col-title">{w.name}</SiteLink>
                  <ul>{splitSubs(w.sub).map((s) => <li key={s}><SiteLink href={subHref(w.id, s)} className="mega-sub">{s}</SiteLink></li>)}</ul>
                </div>
              ))}
              <div className="mega-feature">
                <SiteLink href={landing.megaFeature.href} className="mega-feat-item">
                  <span className="media media--zoom"><img loading="lazy" src={landing.megaFeature.image} alt={landing.megaFeature.alt || ""} /></span>
                  <span className="mega-feat-cap">{landing.megaFeature.caption}</span>
                </SiteLink>
              </div>
            </div>
          </div>
        </li>
  );
  const cmsItems = cmsNavItems(cms);
  const navList = cmsItems ? (
    <ul>
      {cmsItems.map((it) => {
        if (it.mega) return megaItem(it);
        const kids = cmsChildren(it);
        if (kids.length) {
          return (
            <li key={it.id || it.label} className={"cms-has-sub" + (isCurrent(it) ? " is-current" : "")}>
              <SiteLink href={it.href}>{it.label}</SiteLink>
              <div className="cms-sub">
                {kids.map((c) => <SiteLink key={c.id || c.label} href={c.href} className="cms-sub-item">{c.label}</SiteLink>)}
              </div>
            </li>
          );
        }
        return <li key={it.id || it.label} className={isCurrent(it) ? "is-current" : undefined}><SiteLink href={it.href}>{it.label}</SiteLink></li>;
      })}
    </ul>
  ) : (
    <ul>
      {nav.primary.map((it) => it.mega ? megaItem(it) : (
        <li key={it.label} className={isCurrent(it) ? "is-current" : undefined}><SiteLink href={it.href}>{it.label}</SiteLink></li>
      ))}
    </ul>
  );
  const logo = cms && cms.logo ? cms.logo : null;
  const logoHref = logo && logo.href ? logo.href : "lhc-landing-page.html";
  /* keyed: the builder's logo is a NEW element, never the prototype's re-attributed (siteLang
     pins an element's first alt / title and would restore it on change) */
  const logoBody = logo && logo.image ? <img key="cms-logo" className="logo-img" src={logo.image} alt={logo.text || settings.brand} />
    : logo && logo.text ? <span key="cms-text" className="logo-text">{logo.text}</span>
      : <img key="logo" className="logo-img" src={settings.logo} alt={settings.brand} />;

  return (
    <>
      <header className={cls} id="siteHeader" data-testid="site-header">
        <div className="top-bar">
          <div className="top-bar-inner">
            <div className="top-right">
              <button type="button" className="search-link" aria-label="Search" onClick={onSearch}>Search</button>
              <div className="top-langs" aria-label="Language">
                {LANGS.map((l) => {
                  const code = l.toLowerCase();
                  return (
                    <button type="button" key={l} className={code === lang ? "is-active" : undefined}
                      aria-pressed={code === lang} lang={code} onClick={() => setLang(code)}>{l}</button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        <div className="header-inner">
          <div className="header-row">
            <nav className="main-nav main-nav-left" aria-label="Primary left">{navList}</nav>
            <SiteLink href={logoHref} className="logo" aria-label={`${settings.brand} — home`}>
              {logoBody}
            </SiteLink>
            <div className="header-spacer" aria-hidden="true" />
          </div>
          <div className="header-actions">
            <button type="button" className="menu-toggle" id="menuToggle" aria-expanded={mobile} onClick={() => setMobile((v) => !v)}>{mobile ? "Close" : "Menu"}</button>
          </div>
        </div>
      </header>
      <MobileNav open={mobile} onClose={() => setMobile(false)} worlds={worlds} nav={nav} cmsItems={cmsItems} lang={lang} onLang={setLang} />
    </>
  );
}

function MobileNav({ open, onClose, worlds, nav, cmsItems, lang, onLang }) {
  const [sub, setSub] = useState(false);
  const landing = useSiteContent("landing", LANDING);
  /* builder nav: a mega item keeps the Products accordion below; children render inline */
  const items = cmsItems || nav.primary;
  return (
    <div className={"mobile-nav" + (open ? " is-open" : "")} id="mobileNav" aria-hidden={!open}>
      <button type="button" className="mobile-nav-close" id="mobileNavClose" onClick={onClose}>Close</button>
      <nav aria-label="Mobile primary">
        <ul>
          {items.map((it) => it.mega ? (
            <li key={it.label} className={"mobile-has-sub" + (sub ? " is-open" : "")} id="mobileProductsItem">
              <div className="mobile-sub-trigger">
                <SiteLink href={it.href} onClick={onClose}>{it.label}</SiteLink>
                <button type="button" className="mobile-sub-toggle" id="mobileProductsToggle" aria-expanded={sub} aria-controls="mobileProductsPanel" aria-label="Show product categories"
                  onClick={(e) => { e.preventDefault(); setSub((v) => !v); }}><span className="caret" /></button>
              </div>
              <div className="mobile-sub-panel" id="mobileProductsPanel">
                <div className="mobile-sub-panel-inner">
                  {worlds.map((w) => <SiteLink key={w.id} href={worldHref(w.id)} onClick={onClose}>{w.name}</SiteLink>)}
                </div>
              </div>
            </li>
          ) : cmsItems && cmsChildren(it).length ? (
            <li key={it.id || it.label} className="cms-mobile-has-sub">
              <SiteLink href={it.href} onClick={onClose}>{it.label}</SiteLink>
              <div className="cms-sub-mobile">
                {cmsChildren(it).map((c) => <SiteLink key={c.id || c.label} href={c.href} onClick={onClose}>{c.label}</SiteLink>)}
              </div>
            </li>
          ) : (
            <li key={it.label}><SiteLink href={it.href} onClick={onClose}>{it.label}</SiteLink></li>
          ))}
        </ul>
      </nav>
      <div className="mobile-utility">
        {landing.mobileUtility.map((l) => <SiteLink key={l.label} href={l.href} onClick={onClose}>{l.label}</SiteLink>)}
        <div className="mobile-langs">
          {LANGS.map((l) => (
            <button type="button" key={l} className={l === lang ? "is-active" : undefined} aria-pressed={l === lang} onClick={() => onLang && onLang(l)}>{l}</button>
          ))}
        </div>
      </div>
      <div className="mobile-nav-foot">
        <SiteLink href={nav.professional.href} onClick={onClose}>{nav.professional.label}</SiteLink>
        <SiteLink href={nav.contact.href} onClick={onClose}>{nav.contact.label}</SiteLink>
      </div>
    </div>
  );
}

/* ───────────────────────────── search ───────────────────────────── */
/* The prototype's overlay only ever printed "search index not yet connected". This one really
   searches: siteSearch builds an index over every published page (all 89 pieces, the 25 category
   listings, the worlds, materials, projects and the standalone pages) and is dynamically imported
   the first time the overlay opens, so the big piece catalogue never lands in the main bundle. */
export function SearchOverlay({ open, onClose }) {
  const { settings } = useShared();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);          // null = index not loaded yet
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) { setQ(""); return undefined; }
    let alive = true;
    const t = setTimeout(() => inputRef.current && inputRef.current.focus(), 300);
    if (!rows) {
      setLoading(true);
      loadIndex().then((r) => { if (alive) { setRows(r); setLoading(false); } })
        .catch(() => { if (alive) setLoading(false); });
    }
    return () => { alive = false; clearTimeout(t); };
  }, [open, rows]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const results = React.useMemo(() => (rows ? search(rows, q) : []), [rows, q]);
  const groups = React.useMemo(() => groupResults(results), [results]);
  const go = (route) => { onClose(); navigate(route); };
  const submit = (e) => { e.preventDefault(); if (results.length) go(results[0].route); };

  return (
    <div className={"search-overlay" + (open ? " is-open" : "")} id="searchOverlay" aria-hidden={!open}>
      <div className="wrap search-overlay-inner">
        <form id="searchForm" onSubmit={submit} role="search">
          <input ref={inputRef} type="text" id="searchInput" placeholder={settings.search.placeholder} autoComplete="off"
            value={q} onChange={(e) => setQ(e.target.value)} aria-label={settings.search.placeholder} />
          <button type="submit" className="text-link">Search</button>
        </form>
        <button type="button" className="search-overlay-close" id="searchOverlayClose" aria-label="Close search" onClick={onClose}>Close</button>
      </div>
      <div className="wrap search-overlay-result" id="searchOverlayResult" aria-live="polite">
        {!q.trim() ? null : loading ? (
          <p className="body-md">Searching…</p>
        ) : results.length ? (
          <>
            <p className="eyebrow search-count">{results.length === 1 ? "1 result" : results.length + " results"}</p>
            {groups.map((g) => (
              <div className="search-group" key={g.kind}>
                <p className="eyebrow search-group-title">{g.kind}</p>
                <ul className="search-hits">
                  {g.items.map((r) => (
                    <li key={r.route + r.label}>
                      <SiteLink href={r.route} className="search-hit" onClick={onClose}>
                        <span className="search-hit-name">{r.label}</span>
                        {r.sub ? <span className="search-hit-sub">{r.sub}</span> : null}
                      </SiteLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </>
        ) : (
          <p className="body-md search-empty">No results for “{q.trim()}”.</p>
        )}
      </div>
    </div>
  );
}

/* ───────────────────────────── footer ───────────────────────────── */
export function SiteFooter({ home = false }) {
  const { settings, cms } = useShared();
  const f = settings.footer;
  /* the website builder's footer: its columns replace the prototype's link columns and its
     bottom text joins the legal row; the statement row, contact and follow columns (real company
     facts from the content layer) stay */
  const cmsFooter = cms && cms.footer && cms.footer.enabled ? cms.footer : null;
  if (cmsFooter) {
    const cols = (Array.isArray(cmsFooter.columns) ? cmsFooter.columns : []).filter(Boolean);
    return (
      <footer className="site-footer" id="contact" data-testid="site-footer">
        {home ? null : (
          <div className="footer-statement-row">
            <div>
              <p className="eyebrow">{f.eyebrow}</p>
              <h2 className="footer-statement">{f.statement}</h2>
            </div>
            <div className="footer-statement-actions">
              <SiteLink href={f.primary.href} className="btn btn--on-dark">{f.primary.label}</SiteLink>
              <SiteLink href={f.secondary.href} className="text-link">{f.secondary.label}</SiteLink>
            </div>
          </div>
        )}
        <div className="footer-nav-row">
          {cols.map((col, i) => (
            <div className="footer-col cms-footer-col" key={col.id || col.title || i}>
              <h4>{col.title}</h4>
              <ul>{(Array.isArray(col.links) ? col.links : []).filter((l) => l && l.label).map((l, j) => <li key={l.id || l.label || j}><SiteLink href={l.href}>{l.label}</SiteLink></li>)}</ul>
            </div>
          ))}
          <div className="footer-col">
            <h4>{f.contactTitle}</h4>
            <ul>
              <li className="body-md" style={{ color: "rgba(248,246,242,.7)" }}>{settings.address.map((line, i) => <React.Fragment key={i}>{i > 0 ? <br /> : null}{line}</React.Fragment>)}</li>
              <li><SiteLink href={f.contactLink.href}>{f.contactLink.label}</SiteLink></li>
            </ul>
          </div>
          <div className="footer-col">
            <h4>{f.followTitle}</h4>
            <ul>{settings.social.map((s) => <li key={s.label}><a href={s.href} rel="noopener">{s.label}</a></li>)}</ul>
          </div>
        </div>
        <div className="footer-bottom">
          {cmsFooter.bottomText ? <span className="cms-footer-text">{cmsFooter.bottomText}</span> : null}
          <div className="footer-legal">
            <span>{settings.copyright}</span>
            {f.legal.map((l) => <SiteLink key={l.label} href={l.href}>{l.label}</SiteLink>)}
          </div>
        </div>
      </footer>
    );
  }
  return (
    <footer className="site-footer" id="contact" data-testid="site-footer">
      {/* the home film page closes on its own connect section, so it drops the statement row */}
      {home ? null : (
        <div className="footer-statement-row">
          <div>
            <p className="eyebrow">{f.eyebrow}</p>
            <h2 className="footer-statement">{f.statement}</h2>
          </div>
          <div className="footer-statement-actions">
            <SiteLink href={f.primary.href} className="btn btn--on-dark">{f.primary.label}</SiteLink>
            <SiteLink href={f.secondary.href} className="text-link">{f.secondary.label}</SiteLink>
          </div>
        </div>
      )}
      <div className="footer-nav-row">
        {f.columns.map((col) => (
          <div className="footer-col" key={col.title}>
            <h4>{col.title}</h4>
            <ul>{col.links.map((l) => <li key={l.label}><SiteLink href={l.href} {...(l.blank ? { target: "_blank" } : {})}>{l.label}</SiteLink></li>)}</ul>
          </div>
        ))}
        <div className="footer-col">
          <h4>{f.contactTitle}</h4>
          <ul>
            <li className="body-md" style={{ color: "rgba(248,246,242,.7)" }}>{settings.address.map((line, i) => <React.Fragment key={i}>{i > 0 ? <br /> : null}{line}</React.Fragment>)}</li>
            <li><SiteLink href={f.contactLink.href}>{f.contactLink.label}</SiteLink></li>
          </ul>
        </div>
        <div className="footer-col">
          <h4>{f.followTitle}</h4>
          <ul>{settings.social.map((s) => <li key={s.label}><a href={s.href} rel="noopener">{s.label}</a></li>)}</ul>
        </div>
      </div>
      <div className="footer-bottom">
        <div className="footer-legal">
          <span>{settings.copyright}</span>
          {f.legal.map((l) => <SiteLink key={l.label} href={l.href}>{l.label}</SiteLink>)}
        </div>
      </div>
    </footer>
  );
}

/** the hero fade/rise choreography — `is-loaded` a beat after mount, as the prototype does */
export function useHeroLoaded(delay = 120) {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { let t; const raf = requestAnimationFrame(() => { t = setTimeout(() => setLoaded(true), delay); }); return () => { cancelAnimationFrame(raf); clearTimeout(t); }; }, [delay]);
  return loaded;
}

/** the prototype's newsletter / simple forms: swap the form for a confirmation line */
export function useConfirm() {
  const [done, setDone] = useState(false);
  const submit = useCallback((e) => { if (e && e.preventDefault) e.preventDefault(); setDone(true); }, []);
  return [done, submit];
}
